// packages/ui/src/components/Input/Input.tsx
// Phase 15 Plan 02: added variant="pill" + rightAdornment prop

import type { ReactNode, InputHTMLAttributes } from 'react';
import { forwardRef, useState, useId } from 'react';

import { Eye, EyeOff } from 'lucide-react';

import { cn } from '../../lib/cn';
import { LabelHint } from '../LabelHint/LabelHint';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Label text displayed above the input. Consumer passes t() value. */
  label?: string;
  /**
   * Explanation shown in a tooltip via an Info icon next to the label.
   * Consumer passes t() value. Ignored when `label` is absent.
   */
  labelHint?: string;
  /** Placeholder text. Consumer passes t() value. */
  placeholder?: string;
  /** Current input value (controlled). */
  value?: string;
  /** Change handler. */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Error message to display below the input. */
  error?: string;
  /** Helper text shown when there is no error. */
  helperText?: string;
  /** HTML input type. */
  type?:
    | 'text'
    | 'email'
    | 'password'
    | 'number'
    | 'tel'
    | 'url'
    | 'search'
    | 'date'
    | 'datetime-local'
    | 'time';
  /** Disables the input. */
  disabled?: boolean;
  /** Shows a required indicator (*) after the label. */
  required?: boolean;
  /** Element rendered inside the input on the left side. */
  leftIcon?: ReactNode;
  /** Element rendered inside the input on the right side (default variant). */
  rightIcon?: ReactNode;
  /**
   * Element rendered as right adornment inside the input.
   * Used in pill variant for e.g. DocTypeChip (PF/PJ).
   * In default variant, falls back to rightIcon position.
   */
  rightAdornment?: ReactNode;
  /** Size variant affecting height and font size. */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Visual variant.
   * - "default": standard bordered input (existing behavior)
   * - "pill": pill-shaped input with bg-ink/5, rounded-2xl, 52px min-height,
   *   inline left icon (laranja-encanto), right adornment, 16px font (anti-zoom iOS).
   */
  variant?: 'default' | 'pill';
  /** HTML name attribute. */
  name?: string;
  /** HTML id attribute. Will auto-generate if not provided. */
  id?: string;
  /** Additional class name on the wrapper. */
  className?: string;
}

const sizeClasses: Record<NonNullable<InputProps['size']>, string> = {
  sm: 'h-8 px-2 py-1.5 text-sm',
  md: 'h-10 px-3 py-2 text-sm',
  lg: 'h-12 px-4 py-3 text-base',
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    labelHint,
    placeholder,
    value,
    onChange,
    error,
    helperText,
    type = 'text',
    disabled = false,
    required = false,
    leftIcon,
    rightIcon,
    rightAdornment,
    size = 'md',
    variant = 'default',
    name,
    id: externalId,
    className,
    ...rest
  },
  ref,
) {
  const generatedId = useId();
  const inputId = externalId || generatedId;
  const errorId = `${inputId}-error`;
  const helperId = `${inputId}-helper`;
  const hasError = !!error;

  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === 'password';
  const resolvedType = isPassword && showPassword ? 'text' : type;

  const describedBy =
    [hasError ? errorId : null, helperText && !hasError ? helperId : null]
      .filter(Boolean)
      .join(' ') || undefined;

  // ── Pill variant ────────────────────────────────────────────────────────────
  if (variant === 'pill') {
    return (
      <div className={cn('flex w-full flex-col gap-1', className)}>
        {label && (
          <label htmlFor={inputId} className="text-sm font-medium text-ink/70 font-poppins">
            {label}
            {(required || labelHint) && (
              <span className="whitespace-nowrap">
                {required && (
                  <span className="ml-0.5 text-erro-apoio" aria-hidden="true">
                    *
                  </span>
                )}
                {labelHint && <LabelHint hint={labelHint} />}
              </span>
            )}
          </label>
        )}
        <div className="relative w-full">
          {leftIcon && (
            <span
              className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-ink/40"
              aria-hidden="true"
            >
              {leftIcon}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            name={name}
            type={resolvedType}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            aria-invalid={hasError || undefined}
            aria-describedby={describedBy}
            aria-required={required || undefined}
            className={cn(
              // V4 prototype: bg-white + border slate-200, NOT bg-ink/5 fill
              'w-full rounded-2xl bg-white border border-slate-200 text-base font-poppins text-ink',
              // min-h-[52px] ensures tap target ≥44px (D-PHASE14-8)
              'min-h-[52px] py-3 pr-4',
              leftIcon ? 'pl-12' : 'pl-4',
              rightAdornment ? 'pr-16' : 'pr-4',
              'placeholder:text-ink/40',
              'outline-none transition-all',
              'focus:border-laranja-encanto focus:ring-2 focus:ring-laranja-encanto/20',
              hasError && 'border-erro-apoio ring-2 ring-erro-apoio/20',
              disabled && 'cursor-not-allowed bg-slate-50 opacity-60',
            )}
            {...rest}
          />
          {rightAdornment && (
            <span
              className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2"
              aria-hidden="true"
            >
              {rightAdornment}
            </span>
          )}
          {isPassword && (
            <button
              type="button"
              tabIndex={-1}
              className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center text-ink/40 hover:text-ink/60"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          )}
        </div>
        {hasError && (
          <span id={errorId} role="alert" className="text-xs text-erro-apoio mt-0.5">
            {error}
          </span>
        )}
        {!hasError && helperText && (
          <span id={helperId} className="text-xs text-ink/50 mt-0.5">
            {helperText}
          </span>
        )}
      </div>
    );
  }

  // ── Default variant ─────────────────────────────────────────────────────────
  return (
    <div className={cn('flex w-full flex-col gap-1', className)}>
      {label && (
        <label htmlFor={inputId} className="text-sm font-medium text-gray-700 font-body">
          {label}
          {(required || labelHint) && (
            <span className="whitespace-nowrap">
              {required && (
                <span className="ml-0.5 text-error-500" aria-hidden="true">
                  *
                </span>
              )}
              {labelHint && <LabelHint hint={labelHint} />}
            </span>
          )}
        </label>
      )}
      <div
        className={cn(
          'flex items-center rounded-lg border overflow-hidden transition-colors',
          hasError ? 'border-error-500' : 'border-gray-300',
          disabled ? 'bg-gray-50' : 'bg-white',
        )}
      >
        {leftIcon && (
          <span
            className="flex items-center justify-center px-2 text-gray-400 shrink-0"
            aria-hidden="true"
          >
            {leftIcon}
          </span>
        )}
        <input
          ref={ref}
          id={inputId}
          name={name}
          type={resolvedType}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          aria-invalid={hasError || undefined}
          aria-describedby={describedBy}
          aria-required={required || undefined}
          className={cn(
            'w-full flex-1 bg-transparent border-none outline-none placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:cursor-not-allowed font-body',
            sizeClasses[size],
          )}
          {...rest}
        />
        {isPassword && (
          <button
            type="button"
            tabIndex={-1}
            className="flex items-center justify-center px-2 text-gray-400 hover:text-gray-600 shrink-0"
            onClick={() => setShowPassword((prev) => !prev)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        )}
        {!isPassword && (rightIcon || rightAdornment) && (
          <span
            className="flex items-center justify-center px-2 text-gray-400 shrink-0"
            aria-hidden="true"
          >
            {rightIcon ?? rightAdornment}
          </span>
        )}
      </div>
      {hasError && (
        <span id={errorId} role="alert" className="text-xs text-error-500 mt-0.5">
          {error}
        </span>
      )}
      {!hasError && helperText && (
        <span id={helperId} className="text-xs text-gray-400 mt-0.5">
          {helperText}
        </span>
      )}
    </div>
  );
});
